"use client";

import image04 from "@/public/mock_images/image_04.webp";
import image05 from "@/public/mock_images/image_05.webp";
import Image, { StaticImageData } from "next/image";
import { motion } from "motion/react";
import { cn } from "@/lib/utils";
import { Section } from "./ui/section";
import { SectionHeader } from "./ui/section-header";
import { EASE } from "@/lib/motion";

type Props = {
  title: string;
  subtitle: string;
  description: string;
  image1?: StaticImageData;
  image2?: StaticImageData;
  reverse?: boolean;
};

function splitLastWord(text: string): [string, string] {
  const trimmed = text.trim();
  const lastSpace = trimmed.lastIndexOf(" ");
  if (lastSpace === -1) return ["", trimmed];
  return [trimmed.slice(0, lastSpace), trimmed.slice(lastSpace + 1)];
}

export function About({
  title,
  subtitle,
  description,
  image1 = image04,
  image2 = image05,
  reverse = false,
}: Props) {
  const [head, tail] = splitLastWord(title);

  return (
    <Section>
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, ease: EASE }}
          className={cn(
            "relative lg:col-span-7 group",
            reverse ? "lg:order-2" : "lg:order-1",
          )}
        >
          <div className="relative w-full aspect-[4/3] lg:aspect-[5/4] overflow-hidden ring-1 ring-brand-300/30">
            <motion.div
              initial={{ scale: 1.08 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 1.5, ease: EASE }}
              className="absolute inset-0 will-change-transform"
            >
              <Image
                src={image1}
                alt=""
                fill
                className="object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.6,-0.05,0.01,0.99)] group-hover:scale-[1.025]"
                sizes="(max-width: 1024px) 100vw, 58vw"
                placeholder="blur"
              />
            </motion.div>
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-foreground/[0.04]"
            />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.85, ease: EASE, delay: 0.45 }}
            className={cn(
              "hidden lg:block absolute -bottom-14 z-10",
              reverse ? "-left-8" : "-right-8",
            )}
          >
            <div
              className={cn(
                "flex flex-col gap-3",
                reverse ? "items-start" : "items-end",
              )}
            >
              <div className="relative size-44 xl:size-52 overflow-hidden ring-1 ring-brand-300/40 bg-brand-100 transition-all duration-700 ease-[cubic-bezier(0.6,-0.05,0.01,0.99)] group-hover:-translate-y-2">
                <Image
                  src={image2}
                  alt=""
                  fill
                  className="object-cover object-center transition-transform duration-[900ms] ease-[cubic-bezier(0.6,-0.05,0.01,0.99)] group-hover:scale-[1.06]"
                  sizes="220px"
                  placeholder="blur"
                />
              </div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.85, ease: EASE }}
          className={cn(
            "lg:col-span-5",
            reverse ? "lg:order-1" : "lg:order-2",
          )}
        >
          <SectionHeader
            eyebrow={subtitle}
            title={head}
            accent={tail}
            description={description}
            divider
          />
        </motion.div>
      </div>
    </Section>
  );
}
